import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import {
  Palette,
  Monitor,
  Sun,
  Moon,
  RotateCcw,
  Check
} from 'lucide-react';
import { useUserPreferences, ThemeMode, AccentColor } from '@/hooks/useUserPreferences';
import { validateContrast } from '@/lib/a11y';

interface ThemeCustomizerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ACCENT_COLOR_OPTIONS = [
  {
    value: 'gold' as AccentColor,
    label: 'Brand Gold',
    color: '#FFD700',
    description: 'Warm and welcoming'
  },
  {
    value: 'blue' as AccentColor,
    label: 'Church Blue', 
    color: '#2563eb',
    description: 'Professional and trustworthy'
  },
  {
    value: 'emerald' as AccentColor,
    label: 'Emerald',
    color: '#10b981',
    description: 'Natural and growth-focused'
  },
  {
    value: 'slate' as AccentColor,
    label: 'Neutral Slate',
    color: '#64748b',
    description: 'Clean and minimalist'
  }
];

const THEME_MODE_OPTIONS = [
  {
    value: 'light' as ThemeMode,
    label: 'Light',
    description: 'Always use light theme',
    icon: Sun
  },
  {
    value: 'dark' as ThemeMode,
    label: 'Dark',
    description: 'Always use dark theme',
    icon: Moon
  },
  {
    value: 'auto' as ThemeMode,
    label: 'Auto',
    description: 'Follow system preference',
    icon: Monitor
  }
];

export function ThemeCustomizer({ open, onOpenChange }: ThemeCustomizerProps) {
  const {
    preferences,
    isLoading,
    updateThemeMode,
    updateAccentColor,
    resetPreferences
  } = useUserPreferences();

  const handleThemeModeChange = (mode: ThemeMode) => {
    updateThemeMode(mode);
  };

  const handleAccentColorChange = (color: AccentColor) => {
    updateAccentColor(color);
  };

  const handleReset = () => {
    resetPreferences();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Theme Customization
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Theme Mode Section */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Color Mode</Label>
            <RadioGroup
              value={preferences.themeMode}
              onValueChange={handleThemeModeChange}
              className="space-y-2"
            >
              {THEME_MODE_OPTIONS.map((option) => {
                const IconComponent = option.icon;
                return (
                  <div key={option.value} className="flex items-center space-x-3">
                    <RadioGroupItem value={option.value} id={option.value} />
                    <Label 
                      htmlFor={option.value}
                      className="flex items-center gap-3 cursor-pointer flex-1"
                    >
                      <IconComponent className="h-4 w-4" />
                      <div>
                        <div className="font-medium text-sm">{option.label}</div>
                        <div className="text-xs text-muted-foreground">
                          {option.description}
                        </div>
                      </div>
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          </div>

          {/* Accent Color Section */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Accent Color</Label>
            <RadioGroup
              value={preferences.accentColor}
              onValueChange={handleAccentColorChange}
              className="space-y-3"
            >
              {ACCENT_COLOR_OPTIONS.map((option) => (
                <div key={option.value} className="flex items-center space-x-3">
                  <RadioGroupItem value={option.value} id={`color-${option.value}`} />
                  <Label 
                    htmlFor={`color-${option.value}`}
                    className="flex items-center gap-3 cursor-pointer flex-1"
                  >
                    <div className="flex-shrink-0">
                      <div 
                        className="w-6 h-6 rounded-full border-2 border-muted-foreground/20"
                        style={{ backgroundColor: option.color }}
                      />
                    </div>
                    <div>
                      <div className="font-medium text-sm flex items-center gap-2">
                        {option.label}
                        {validateContrast(option.color, '#ffffff') && validateContrast(option.color, '#000000') ? (
                          <Check className="h-3 w-3 text-green-600" />
                        ) : (
                          <span className="text-xs text-amber-600">⚠</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {option.description}
                      </div>
                    </div>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Preview Section */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Preview</Label>
            <Card className="border-dashed">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Sample Card</CardTitle>
                  <Badge variant="secondary" className="text-xs">
                    Preview
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    This is how UI elements will look with your selected theme.
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1">
                      Primary Button
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1">
                      Secondary Button
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary" />
                    <span>Accent color applied</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={isLoading}
              className="flex items-center gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              Reset Defaults
            </Button>
            <Button
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
